import api from './api';

/**
 * Upload a File or Blob to R2 via the Helix API.
 * Returns a persistent public URL that survives page refresh.
 *
 * @param {File|Blob} file
 * @param {object} [opts]
 * @param {(progress: number) => void} [opts.onProgress] — 0..1
 * @returns {Promise<{ url: string, key: string, contentType: string, size: number }>}
 */
export async function uploadMedia(file, opts = {}) {
    if (!file) throw new Error('uploadMedia: file is required');

    const formData = new FormData();
    formData.append('file', file, file.name || 'upload');
    // Optional: tag the upload to a project so it shows up in that project's
    // Media tab AND the Personal Library. Falls back to localStorage's
    // current project if not explicitly provided.
    const projectId = opts.projectId
        ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('helix_current_project') : null);
    if (projectId) {
        formData.append('projectId', projectId);
    }

    const response = await api.post('/media/upload', formData, {
        // Let the browser set the multipart boundary header
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
            if (opts.onProgress && e.total) {
                opts.onProgress(e.loaded / e.total);
            }
        },
    });

    return response.data?.data || response.data;
}

/**
 * Drop-in replacement for URL.createObjectURL(file) when you want the URL
 * to persist across page refreshes. Returns the persistent URL on success;
 * falls back to a blob URL on failure (e.g. offline, server unreachable).
 *
 * @param {File|Blob} file
 * @returns {Promise<{ url: string, persistent: boolean }>}
 */
export async function createPersistentUrl(file) {
    try {
        const result = await uploadMedia(file);
        return { url: result.url, persistent: true };
    } catch (err) {
        console.warn('[media] Persistent upload failed, falling back to blob URL', err);
        return { url: URL.createObjectURL(file), persistent: false };
    }
}

const mediaService = { uploadMedia, createPersistentUrl };
export default mediaService;
